// packages/studio/web/src/repos/browser/NewFileModal.tsx
//
// "New file" dialog for the repo browser. Editor-first: instead of pushing
// from a local clone, the user names a file here and is taken straight to the
// Monaco editor with an empty buffer. The first commit on save upserts the
// new file (the editor already handles a path that isn't in the seeded VFS).

import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "../../ui/Modal";
import { Field } from "../../ui/Field";
import { TextInput } from "../../ui/TextInput";
import { Button } from "../../ui/Button";
import { canonicalPath, isWorkspacePath } from "@archlang/viewer-core/workspace-file";
import { repoHome } from "../../lib/paths";
import { useTranslation } from "../../i18n";

// What this dialog accepts is THE workspace-file law (viewer-core
// `workspace-file`), not a mirror of it: `.arch` sources, the
// `package.archspace` manifest, `.md`/`.markdown` documents — validated segment
// by segment because what the user types becomes git tree-entry names verbatim
// (`commit-impl.ts` splits on "/" with no normalization), and canonicalized so
// `PACKAGE.ARCHSPACE` is the file they obviously mean rather than an inert
// lookalike the byte-exact loaders skip.
//
// Unlike the nav — which also mints free-form files ("Other file…") — this
// dialog stays curated: it exists to open a NEW MODEL in the browser editor.

export interface NewFileModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly wsSlug: string;
  readonly repoSlug: string;
  readonly ref: string;
  /**
   * When set (editor context), create the file in place — registers it in the
   * working set so it shows in the tree and persists, rather than just
   * navigating to a not-yet-existing path. Falls back to navigation (repo
   * browser, where no editor is mounted).
   */
  readonly onCreate?: (path: string) => void;
}

export function NewFileModal({ open, onClose, wsSlug, repoSlug, onCreate }: NewFileModalProps) {
  const { t } = useTranslation("repos");
  const { t: tCommon } = useTranslation("common");
  const nav = useNavigate();
  const [name, setName] = useState("");

  // What is VALIDATED is what gets CREATED: the canonical spelling. Accepting
  // `PACKAGE.ARCHSPACE` and then creating it verbatim would mint the inert
  // lookalike the loaders (which match byte-exactly) skip.
  const trimmed = canonicalPath(name.trim());
  const valid = isWorkspacePath(trimmed);
  const touched = trimmed.length > 0;

  function handleClose() {
    setName("");
    onClose();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    handleClose();
    if (onCreate) onCreate(trimmed);
    // No editor mounted (repo browser): hand the path to the editor route,
    // which opens it as a new buffer. `?new=` survives the unnamed-draft
    // redirect — dropping it opened an unrelated file instead.
    else nav(`${repoHome(wsSlug, repoSlug)}?new=${encodeURIComponent(trimmed)}`);
  }

  return (
    <Modal open={open} onClose={handleClose} title={t("newFile.title")}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <p className="text-sm text-slate-400">
          {t("newFile.intro")}
        </p>
        <Field
          label={t("newFile.nameLabel")}
          error={touched && !valid ? t("newFile.invalid") : undefined}
        >
          {({ id, ...aria }) => (
            <TextInput
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("newFile.namePlaceholder")}
              autoFocus
              hasError={touched && !valid}
              {...aria}
            />
          )}
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" type="button" onClick={handleClose}>
            {tCommon("cancel")}
          </Button>
          <Button variant="primary" type="submit" disabled={!valid}>
            {t("newFile.create")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
